import { AVATAR_MATRIX, type AvatarEntry, type Resolucion, type Plano } from '../constants/avatar-matrix';

// ── Index para O(1) lookup ──
const AVATAR_INDEX = new Map(AVATAR_MATRIX.map(a => [`${a.vestuario}|${a.ambiente}|${a.resolucion}|${a.plano}`, a]));

/** Vestuarios únicos disponibles */
export const getVestuarios = (): string[] => {
  return Array.from(new Set(AVATAR_MATRIX.map(a => a.vestuario)));
};

/** Ambientes disponibles para un vestuario */
export const getAmbientes = (vestuario: string): string[] => {
  return Array.from(new Set(AVATAR_MATRIX.filter(a => a.vestuario === vestuario).map(a => a.ambiente)));
};

/** Planos disponibles para una combinación de vestuario + ambiente + resolución */
export const getPlanos = (vestuario?: string, ambiente?: string, resolucion?: Resolucion): Plano[] => {
  if (!vestuario || !ambiente || !resolucion) return ['medio', 'pecho', 'entero', 'lejano'];
  return AVATAR_MATRIX
    .filter(a => a.vestuario === vestuario && a.ambiente === ambiente && a.resolucion === resolucion)
    .map(a => a.plano);
};

/** Busca un entry por combinación exacta. Retorna null si no existe. */
export const getAvatarEntry = (
  vestuario: string,
  ambiente: string,
  resolucion: Resolucion,
  plano: Plano
): AvatarEntry | null => {
  return AVATAR_INDEX.get(`${vestuario}|${ambiente}|${resolucion}|${plano}`) ?? null;
};

/** Shortcut: retorna solo el photoId o null */
export const getPhotoId = (
  vestuario: string,
  ambiente: string,
  resolucion: Resolucion,
  plano: Plano
): string | null => {
  return getAvatarEntry(vestuario, ambiente, resolucion, plano)?.photoId ?? null;
};

/** Todos los ambientes únicos disponibles (sin filtrar por vestuario) */
export const getAllAmbientes = (): string[] => {
  return Array.from(new Set(AVATAR_MATRIX.map(a => a.ambiente)));
};

/** Vestuarios disponibles para un ambiente */
export const getVestuariosByAmbiente = (ambiente: string): string[] => {
  return Array.from(new Set(AVATAR_MATRIX.filter(a => a.ambiente === ambiente).map(a => a.vestuario)));
};

/** Preview URL para el selector de entorno. Usa las imágenes de entorno sin avatar. */
const ENTORNO_PREVIEW_BASE = `${process.env.REACT_APP_MEDIA_DOMAIN}/avatars/PREVIEWS/ENTORNOS`;

export const getAmbientePreviewAny = (ambiente: string): string => {
  const fileName = ambiente.replace(/ /g, '-');
  return `${ENTORNO_PREVIEW_BASE}/${fileName}.jpg`;
};

/**
 * Preview URL para el selector de vestuario (segundo nivel).
 * Muestra el vestuario en el ambiente ya seleccionado, resolución 16:9, plano medio.
 */
export const getVestuarioPreviewForAmbiente = (vestuario: string, ambiente: string): string | undefined => {
  return getAvatarEntry(vestuario, ambiente, '16:9', 'medio')?.url;
};

/**
 * Preview URL para el selector de plano.
 * Muestra la combinación exacta con vestuario + ambiente seleccionados.
 */
export const getPlanoPreview = (
  vestuario: string,
  ambiente: string,
  resolucion: Resolucion,
  plano: Plano
): string | undefined => {
  return getAvatarEntry(vestuario, ambiente, resolucion, plano)?.url;
};

/**
 * Mapea resolución del selector (fullhd/vertical) a Resolucion de la matrix.
 */
export const resolutionToAspectRatio = (resolution: string): Resolucion => {
  return resolution === 'vertical' ? '9:16' : '16:9';
};
